// src/hooks/usePremiumAccess.js
// ─────────────────────────────────────────────────────────────────────
// Centralized premium-access hook.  Every premium-gating decision in the
// app should flow through this single module so the logic is never
// duplicated across components.
// ─────────────────────────────────────────────────────────────────────
import { useMemo, useCallback, useSyncExternalStore } from 'react';
import { useSubscription, isPaidTier, isBasicTier, isProTier, getUserTier } from '../contexts/SubscriptionContext';
import { getTrialStatus } from './useTrialAccess';
import { useAuth } from '../contexts/AuthContext';

// ── Constants ────────────────────────────────────────────────────────

// TEMP: internal premium gating preview for alan@docuhounds.com only.
// Remove this block and replace with a proper feature flag once rollout is confirmed.
const PREVIEW_ALLOWED_EMAIL = 'alan@docuhounds.com';
const PREVIEW_STORAGE_KEY = 'premium_gating_preview_enabled';

/**
 * Returns true only for the single internal preview user (alan@docuhounds.com).
 */
export function isPremiumGatingPreviewUser(user) {
  if (!user) return false;
  const email = (typeof user.email === 'string' ? user.email : '').trim().toLowerCase();
  return email === PREVIEW_ALLOWED_EMAIL;
}

/**
 * Reads the localStorage toggle value. Only meaningful for the preview user.
 */
export function isPremiumGatingPreviewEnabled() {
  try {
    return localStorage.getItem(PREVIEW_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

// ── Module-level subscription store for preview toggle ───────────────
// Using useSyncExternalStore ensures ALL components calling usePremiumAccess()
// see the same preview value and re-render atomically when it changes.
let _previewListeners = new Set();

function _subscribeToPreview(callback) {
  _previewListeners.add(callback);
  return () => _previewListeners.delete(callback);
}

function _getPreviewSnapshot() {
  return isPremiumGatingPreviewEnabled();
}

function _notifyPreviewChanged() {
  // Schedule notification in a microtask so React processes all state
  // updates in one batch after the current call stack completes.
  Promise.resolve().then(() => {
    _previewListeners.forEach(l => l());
  });
}

/**
 * Writes the localStorage toggle value and notifies all subscribers.
 */
export function setPremiumGatingPreviewEnabled(enabled) {
  try {
    if (enabled) {
      localStorage.setItem(PREVIEW_STORAGE_KEY, '1');
    } else {
      localStorage.removeItem(PREVIEW_STORAGE_KEY);
    }
  } catch {
    // localStorage unavailable — silently ignore
  }
}

// Tool → minimum required tier mapping
const TOOL_REQUIRED_TIER = {
  chat:       'basic',
  quizzes:    'basic',
  flashcards: 'basic',
  podcasts:   'pro',
  audiobooks: 'pro',
};

// ── Pure helpers (no hooks – safe to call anywhere) ──────────────────

/**
 * Determines whether an admin-granted complimentary subscription is active.
 * The backend sets provider = 'admin' and status = 'COMPLIMENTARY' when an admin
 * grants pro access via /api/users/:id/grant-pro-access.
 */
export function isAdminGrantedPremium(subscription) {
  if (!subscription) return false;
  const status = typeof subscription.status === 'string'
    ? subscription.status.toLowerCase()
    : '';
  const provider = typeof subscription.provider === 'string'
    ? subscription.provider.toLowerCase()
    : '';
  return provider === 'admin' && status === 'complimentary';
}

/**
 * Resolve the trial-start timestamp from the user profile.
 *
 * The backend sets firstLoginAt on first login after deploy.
 * Existing users who log in after this feature ships will automatically
 * get their firstLoginAt set, which starts their 7-day trial.
 *
 * No frontend-side backfill logic — the backend handles it.
 * If firstLoginAt is missing, the user simply has no trial.
 *
 * @param {Object|null} user
 * @returns {string|number|null}
 */
export function resolveTrialStart(user) {
  if (!user) return null;
  return user.firstLoginAt || user.first_login_at || null;
}

/**
 * Core access computation – pure function, no React hooks.
 *
 * @param {Object|null} user         – from AuthContext
 * @param {Object|null} subscription – from SubscriptionContext
 * @param {boolean}     previewGating – when false, canCreate always returns true (production behavior)
 * @returns {PremiumAccessState}
 */
export function getPremiumAccessState(user, subscription, previewGating = false) {
  const hasPaid = isPaidTier(subscription);
  const hasAdmin = isAdminGrantedPremium(subscription);
  const trialStart = resolveTrialStart(user);
  const trial = getTrialStatus(trialStart);

  const hasPremiumAccess = hasPaid || hasAdmin || trial.isInFreeTrial;
  const tier = hasAdmin ? 'pro' : getUserTier(subscription);

  // Trial is "expired" only if there was a valid trial that has ended
  const trialExpired = !!(trialStart && !trial.isInFreeTrial);

  // ── TEMP: Preview simulation for alan@docuhounds.com ──────────
  // When previewGating is true, simulate an expired free-tier user.
  // This overrides ALL real entitlements so the paywall actually appears.
  // canView() still returns true so history remains accessible.
  if (previewGating) {
    const canCreate = (_toolType) => false;
    const canView = (_toolType) => true;

    return {
      hasPaidSubscription: false,
      hasAdminGrantedPremium: false,
      isInFreeTrial: false,
      trialExpired: true,
      hasPremiumAccess: false,
      trialEndsAt: null,
      daysRemaining: 0,
      hoursRemaining: 0,
      subscriptionTier: 'free',
      tier: 'free',
      canCreate,
      canView,
    };
  }

  /**
   * Can the user CREATE new content for a given tool?
   *
   * When previewGating is false (production mode), always returns true
   * so existing behavior is unchanged for all non-preview users.
   */
  const canCreate = (_toolType) => true;

  /**
   * Can the user VIEW previously generated content for a given tool?
   *
   * Per spec Section 4 & 9A: always allowed if content already exists,
   * even after trial expiry.
   */
  const canView = (_toolType) => true;

  return {
    hasPaidSubscription: hasPaid,
    hasAdminGrantedPremium: hasAdmin,
    isInFreeTrial: trial.isInFreeTrial,
    trialExpired,
    hasPremiumAccess,
    trialEndsAt: trial.trialEndsAt,
    daysRemaining: trial.daysRemaining,
    hoursRemaining: trial.hoursRemaining,
    subscriptionTier: tier,
    tier,
    canCreate,
    canView,
  };
}

// ── React Hook ───────────────────────────────────────────────────────

/**
 * usePremiumAccess – THE single hook every gated component should use.
 *
 * Returns everything needed for premium gating without any component
 * needing to import or combine multiple helpers.
 *
 * TEMP: The new gating logic only activates when alan@docuhounds.com
 * has the preview toggle enabled. For everyone else canCreate() → true.
 */
export default function usePremiumAccess() {
  const { user } = useAuth();
  const { subscription, initialized } = useSubscription();

  const isPreviewUser = isPremiumGatingPreviewUser(user);

  // useSyncExternalStore guarantees ALL components calling this hook
  // see the same preview value and re-render when it changes.
  const previewOn = useSyncExternalStore(_subscribeToPreview, _getPreviewSnapshot);

  // previewGating is true ONLY for alan@docuhounds.com with the toggle enabled
  const previewGating = isPreviewUser && previewOn;

  const togglePreview = useCallback(() => {
    const current = isPremiumGatingPreviewEnabled();
    setPremiumGatingPreviewEnabled(!current);
    _notifyPreviewChanged();
  }, []);

  const access = useMemo(
    () => getPremiumAccessState(user, subscription, previewGating),
    [user, subscription, previewGating]
  );

  // TEMP: debug logging for premium gating preview — remove when rolling out
  if (isPreviewUser) {
    console.log('[PremiumAccess Preview]', {
      email: user?.email,
      previewToggleEnabled: previewOn,
      previewGating,
      hasPaidSubscription: access.hasPaidSubscription,
      hasAdminGrantedPremium: access.hasAdminGrantedPremium,
      isInFreeTrial: access.isInFreeTrial,
      hasPremiumAccess: access.hasPremiumAccess,
      tier: access.tier,
      canCreateChat: access.canCreate('chat'),
    });
  }

  return {
    ...access,
    initialized,
    // TEMP: preview helpers for the internal toggle UI
    isPreviewUser,
    previewGating,
    togglePreview,
  };
}
